import { createFileRoute, redirect } from "@tanstack/react-router";
import { allLegalDocuments } from "content-collections";
import { z } from "zod";
import { LegalDocumentPage } from "@/components/misc/legal-document-page";
import { getLegalVersion } from "@/lib/legal-versions";
import { isLegalDocumentModuleEnabled } from "@/lib/modules";
import { seo } from "@/utils/seo";

export function isTanStackRedirectError(error: unknown): boolean {
	return Boolean(
		error &&
			typeof error === "object" &&
			"redirect" in error &&
			(error as { redirect?: unknown }).redirect,
	);
}

export const Route = createFileRoute("/_marketing/legal/$slug/$versionId")({
	component: RouteComponent,
	beforeLoad: ({ params }) => {
		try {
			const slug = z.string().parse(params.slug);
			const versionId = z.string().parse(params.versionId);
			const document = getLegalVersion(slug, versionId, allLegalDocuments);
			if (!document || !isLegalDocumentModuleEnabled(document.module)) {
				throw redirect({ to: "/legal" });
			}
			if (document.isCurrent) {
				throw redirect({ to: "/legal/$slug", params: { slug } });
			}
			return { document };
		} catch (error) {
			if (isTanStackRedirectError(error)) {
				throw error;
			}
			throw redirect({ to: "/legal" });
		}
	},
	head: ({ params: { slug, versionId } }) => {
		const document = getLegalVersion(slug, versionId, allLegalDocuments);
		const lastUpdatedIso = document?.lastUpdated
			? new Date(document.lastUpdated as string | Date).toISOString()
			: undefined;
		return {
			...seo({
				title: document?.title
					? `${document.title} (archived) - Legal and Security`
					: "Legal and Security",
				description: document?.summary,
				keywords: "legal, privacy, security, terms, subprocessors",
				type: "Article",
				datePublished: lastUpdatedIso,
				dateModified: lastUpdatedIso,
			}),
		};
	},
});

export function RouteComponent() {
	const { document } = Route.useRouteContext();

	return (
		<LegalDocumentPage
			key={`${document.documentId}-${document.versionId}`}
			document={document}
			allDocuments={allLegalDocuments}
		/>
	);
}
